import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PixelBackground from '@/components/PixelBackground';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import HomePage from '@/pages/HomePage';
import Seo from '@/components/Seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { BASE_URL, getHreflangLinks, getPageMeta } from '@/seo/meta';
import { stripLanguagePrefix, withLanguagePrefix } from '@/lib/locale';

export default function Home() {
  const location = useLocation();
  const { language } = useLanguage();
  const [headerTransform, setHeaderTransform] = useState('');
  const sectionsRef = useRef<HTMLElement[]>([]);
  const locale = language === 'en' ? 'en' : 'el';
  const meta = getPageMeta('home', undefined, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const moveX = (e.clientX - window.innerWidth / 2) / 50;
      const moveY = (e.clientY - window.innerHeight / 2) / 50;

      setHeaderTransform(`translate(${moveX}px, ${moveY}px)`);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleScroll = useCallback(() => {
    const scrollPosition = window.scrollY + window.innerHeight * 0.8;

    sectionsRef.current.forEach((section) => {
      const sectionTop = section.offsetTop;

      if (scrollPosition > sectionTop) {
        section.classList.add('visible');
      }
    });
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll('section');
    sectionsRef.current = Array.from(sections) as HTMLElement[];

    sectionsRef.current.forEach((section) => {
      section.classList.add('fade-in');
    });

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div className="relative min-h-screen">
      <Seo
        title={meta.title}
        description={meta.description}
        url={canonicalUrl}
        image={meta.image}
        hreflang={hreflang}
      />
      <PixelBackground />
      <Header variant="simple" />
      <div
        className="relative z-10"
        style={{ transform: headerTransform, transition: 'transform 0.5s ease-out' }}
      >
        <HomePage />
        <Footer />
      </div>
    </div>
  );
}
